import * as THREE from 'three';
import {
  cellBounds,
  columnLabel,
  createGridSpec,
  gridColumns,
  gridIsLabelled,
  gridRows,
  type GridSpec,
} from '@/site/SiteGrid';

/**
 * The reference grid, drawn.
 *
 * ## Why it is draped rather than flat
 *
 * A grid on the y=0 plane is a grid for a site that has no ground, and this app's whole first act is
 * sculpting one. The moment a berm goes in, a flat grid is half-buried and half-floating, and the
 * cell you are looking at is not the cell the line appears to be over. So every line is subdivided
 * and sampled against the terrain: the grid lies *on* the garden, which is the only way a cell name
 * means the same place to the eye as it does to {@link cellAt}.
 *
 * ## Why the labels are at the margins
 *
 * A letter painted in the middle of each cell is the clearest thing to read and the fastest way to
 * turn a site into a spreadsheet. The labels run along the site's edges instead — north and south for
 * the columns, west and east for the rows — so the garden itself stays uncovered and a cell is read
 * the way a map is read, by tracking in from two edges. Both opposite edges are labelled because the
 * camera orbits, and a label set you have to fly around to read is one you stop using.
 *
 * The naming itself is not decided here — see {@link GridSpec} and `@/site/SiteGrid`. This is a view.
 */

export interface GridOverlayOptions {
  /** Ground height sampler, so the lines follow the terrain. Omitted means a flat grid at y=0. */
  sampleHeight?: (x: number, z: number) => number;
  color?: THREE.ColorRepresentation;
  /** Colour of the site boundary, drawn heavier than the interior lines. */
  edgeColor?: THREE.ColorRepresentation;
  labelColor?: string;
}

/** Metres between sampled points along a grid line. Small enough to follow a berm's shoulder. */
const DRAPE_STEP_METERS = 0.75;

/** How far the lines float above the ground, so they read without z-fighting the surface. */
const LIFT_METERS = 0.06;

export class GridOverlay {
  /** Add this to the scene's overlay root. */
  readonly object: THREE.Group;

  private spec: GridSpec;
  private lines: THREE.LineSegments | null = null;
  private edge: THREE.LineSegments | null = null;
  private readonly labels: THREE.Sprite[] = [];
  private readonly disposables: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[] = [];

  constructor(
    widthMeters: number,
    lengthMeters: number,
    private options: GridOverlayOptions = {},
  ) {
    this.object = new THREE.Group();
    this.object.name = 'site-grid';
    // Off by default: the grid is a reference you reach for, not the state you design in.
    this.object.visible = false;
    this.spec = createGridSpec(widthMeters, lengthMeters);
    this.build();
  }

  /** The grid's naming frame — what the wand is told about, and what `cellAt` is called with. */
  get gridSpec(): GridSpec {
    return this.spec;
  }

  get visible(): boolean {
    return this.object.visible;
  }

  setVisible(visible: boolean): void {
    this.object.visible = visible;
  }

  toggle(): boolean {
    this.object.visible = !this.object.visible;
    return this.object.visible;
  }

  /**
   * Re-fit the grid to a new site.
   *
   * Takes the ground sampler in the same call rather than in a setter of its own: new ground and a
   * new footprint always arrive together, and separating them would rebuild the geometry twice for
   * one event. Spacing defaults to whatever suits the new size.
   */
  setSite(
    widthMeters: number,
    lengthMeters: number,
    options?: { spacingMeters?: number; sampleHeight?: (x: number, z: number) => number },
  ): void {
    this.spec = createGridSpec(widthMeters, lengthMeters, options?.spacingMeters);
    if (options?.sampleHeight) this.options = { ...this.options, sampleHeight: options.sampleHeight };
    this.build();
  }

  setSpacing(spacingMeters: number): void {
    if (spacingMeters === this.spec.spacingMeters) return;
    this.spec = { ...this.spec, spacingMeters };
    this.build();
  }

  /**
   * Re-drape onto ground that has moved. Cheap enough to call after a sculpt stroke — it rewrites the
   * existing position buffer rather than rebuilding geometry or labels.
   */
  refreshHeights(): void {
    const sample = this.options.sampleHeight;
    if (!sample || !this.lines) return;
    for (const target of [this.lines, this.edge]) {
      const position = target?.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!position) continue;
      for (let i = 0; i < position.count; i += 1) {
        position.setY(i, sample(position.getX(i), position.getZ(i)) + LIFT_METERS);
      }
      position.needsUpdate = true;
      target?.geometry.computeBoundingSphere();
    }
  }

  private build(): void {
    this.clear();
    const columns = gridColumns(this.spec);
    const rows = gridRows(this.spec);
    const halfW = this.spec.widthMeters / 2;
    const halfL = this.spec.lengthMeters / 2;
    const step = this.spec.spacingMeters;

    // Interior lines only — the perimeter is drawn separately and heavier, so the site's own edge
    // never reads as just another cell division.
    const interior: number[] = [];
    for (let column = 1; column < columns; column += 1) {
      this.drapeLine(interior, -halfW + column * step, -halfL, -halfW + column * step, halfL);
    }
    for (let row = 1; row < rows; row += 1) {
      this.drapeLine(interior, -halfW, -halfL + row * step, halfW, -halfL + row * step);
    }
    this.lines = this.segments(interior, this.options.color ?? 0x2f3a2a, 0.32);

    const border: number[] = [];
    this.drapeLine(border, -halfW, -halfL, halfW, -halfL);
    this.drapeLine(border, halfW, -halfL, halfW, halfL);
    this.drapeLine(border, halfW, halfL, -halfW, halfL);
    this.drapeLine(border, -halfW, halfL, -halfW, -halfL);
    this.edge = this.segments(border, this.options.edgeColor ?? 0x1d2a18, 0.6);

    this.object.add(this.lines, this.edge);
    this.buildLabels(columns, rows, halfW, halfL);
  }

  /**
   * Emit one grid line as a chain of short segments following the ground.
   *
   * The subdivision is by distance, not by cell, so a 20 m grid over rolling ground is sampled as
   * finely as a 1 m one — the drape has to track the terrain, which knows nothing about the spacing.
   */
  private drapeLine(out: number[], x0: number, z0: number, x1: number, z1: number): void {
    const sample = this.options.sampleHeight;
    const length = Math.hypot(x1 - x0, z1 - z0);
    const steps = sample ? Math.max(1, Math.ceil(length / DRAPE_STEP_METERS)) : 1;
    let previous = this.pointAt(x0, z0, x1, z1, 0, sample);
    for (let i = 1; i <= steps; i += 1) {
      const next = this.pointAt(x0, z0, x1, z1, i / steps, sample);
      out.push(previous[0], previous[1], previous[2], next[0], next[1], next[2]);
      previous = next;
    }
  }

  private pointAt(
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    t: number,
    sample: ((x: number, z: number) => number) | undefined,
  ): [number, number, number] {
    const x = x0 + (x1 - x0) * t;
    const z = z0 + (z1 - z0) * t;
    return [x, (sample?.(x, z) ?? 0) + LIFT_METERS, z];
  }

  private segments(points: number[], color: THREE.ColorRepresentation, opacity: number): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    this.disposables.push(geometry, material);
    return new THREE.LineSegments(geometry, material);
  }

  /**
   * Column letters along the north and south edges, row numbers along the west and east ones.
   *
   * Skipped entirely below a metre of spacing: at that size the labels overlap into a smear, and a
   * grid you can count squares on is more useful than one wearing unreadable text.
   */
  private buildLabels(columns: number, rows: number, halfW: number, halfL: number): void {
    if (typeof document === 'undefined' || !gridIsLabelled(this.spec)) return;
    // Inside the boundary, at the centre of the edge cell it names — not out in the margin. A margin
    // label reads better in isolation but sits exactly in the band the top view crops and the side
    // docks cover, so it vanishes in plan, which is the view a grid is for. Sitting in the cell also
    // makes the label a statement about that cell rather than a tick on a ruler. Capped, so a 20 m
    // grid does not park its letters ten metres into the garden.
    const inset = Math.min(2.5, this.spec.spacingMeters / 2);
    // Sized against the cell, so the letters stay in proportion on a 1 m grid and a 20 m one, and
    // capped: these are world-space sprites, and an unbounded one is a billboard in the foreground.
    const size = clamp(this.spec.spacingMeters * 0.45, 0.8, 2.6);

    for (let column = 0; column < columns; column += 1) {
      const { x0, x1 } = cellBounds(this.spec, column, 0);
      const x = (x0 + x1) / 2;
      this.addLabel(columnLabel(column), x, -halfL + inset, size);
      this.addLabel(columnLabel(column), x, halfL - inset, size);
    }
    for (let row = 0; row < rows; row += 1) {
      const { z0, z1 } = cellBounds(this.spec, 0, row);
      const z = (z0 + z1) / 2;
      this.addLabel(String(row + 1), -halfW + inset, z, size);
      this.addLabel(String(row + 1), halfW - inset, z, size);
    }
  }

  private addLabel(text: string, x: number, z: number, size: number): void {
    const texture = this.labelTexture(text);
    if (!texture) return;
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      // The labels sit outside the site footprint, where nothing should hide them; a label occluded
      // by the terrain it is labelling is a label that fails exactly when the ground is interesting.
      depthTest: false,
      depthWrite: false,
    });
    this.disposables.push(texture, material);
    const sprite = new THREE.Sprite(material);
    sprite.position.set(x, (this.options.sampleHeight?.(x, z) ?? 0) + size * 0.6, z);
    sprite.scale.set(size, size, 1);
    sprite.renderOrder = 12;
    this.labels.push(sprite);
    this.object.add(sprite);
  }

  private labelTexture(text: string): THREE.CanvasTexture | null {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.font = 'bold 68px system-ui, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    // A dark halo under the glyph: the grid is read against grass, gravel and sky in turn, and a
    // single fill colour is illegible against at least one of them.
    context.lineWidth = 10;
    context.strokeStyle = 'rgba(12, 20, 10, 0.85)';
    context.strokeText(text, 64, 68);
    context.fillStyle = this.options.labelColor ?? '#f2f6ea';
    context.fillText(text, 64, 68);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  private clear(): void {
    this.object.clear();
    this.labels.length = 0;
    this.lines = null;
    this.edge = null;
    for (const item of this.disposables) item.dispose();
    this.disposables.length = 0;
  }

  dispose(): void {
    this.clear();
    this.object.removeFromParent();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
